import {useIntl} from 'react-intl';
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {Calendar} from "primereact/calendar";
import {InputNumber} from "primereact/inputnumber";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {
    InvoicePenaltyHistoryModel,
    InvoiceWithPayment,
    PenaltyRepaymentModel,
    UnprocessedBankTransactionResponse
} from "../../lib/types/types";
import useSWR from "swr";
import {InvoiceService} from "../../lib/services/invoice-service";
import {useToast} from "../../lib/hooks/useToast";
import {ReactiveTable} from "react-ive-tables";
import moment from "moment";
import {InputText} from "primereact/inputtext";
import {FormEvent, Nullable} from "primereact/ts-helpers";

interface Props {
    selectedInvoice: InvoiceWithPayment;
    closeDialog: () => void;
    previewOnly: boolean;
    bankTransaction?: UnprocessedBankTransactionResponse;
}

const PenaltyPaymentForInvoice: React.FC<Props> = props => {
    const {formatMessage: f} = useIntl();
    const didMountRef = useRef(false);
    const toastHelpers = useToast();
    const invoiceService = new InvoiceService(f, toastHelpers);

    const [selectedDateFormPenaltyPayments, setSelectedDateFormPenaltyPayments] = useState<Nullable<Date>>(new Date());
    const [amountForPenaltyPayments, setAmountForPenaltyPayments] = useState<number>();
    const [selectedPenaltyHistory, setSelectedPenaltyHistory] = useState<InvoicePenaltyHistoryModel>();
    const [minDate, setMinDate] = useState<Date>(new Date());
    const [note, setNote] = useState<string>();

    const {data: invoicePenaltyHistory} = useSWR<InvoicePenaltyHistoryModel[], Error>(props.selectedInvoice.id ? `/invoices/${props.selectedInvoice.id}/penalties` : null);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            setSelectedDateFormPenaltyPayments(new Date())
        }
    }, []);

    useEffect(() => {
        if(props.bankTransaction){
            const date = new Date(props.bankTransaction.valueDate);
            handlePenaltyPaymentDateSelection({value: date} as FormEvent<Date>);
        }
    }, [props.bankTransaction]);

    useEffect(() => {
        if (!props.selectedInvoice) return;
        setAmountForPenaltyPayments(props.selectedInvoice.penalty);
    }, [props.selectedInvoice])

    useEffect(() => {
        if (!invoicePenaltyHistory || invoicePenaltyHistory.length === 0) return;
        setMinDate(new Date(invoicePenaltyHistory[invoicePenaltyHistory.length - 1].dateCreated));
    }, [invoicePenaltyHistory])

    const closeInvoicePenaltiesDialog = () => {
        setSelectedDateFormPenaltyPayments(new Date());
        setAmountForPenaltyPayments(undefined);
        props.closeDialog();
    }

    const payPenaltiesForInvoiceForDate = () => {
        if (amountForPenaltyPayments === undefined || amountForPenaltyPayments === null || !props.selectedInvoice || !props.selectedInvoice.id) return;
        const data: PenaltyRepaymentModel = {
            invoicePenaltyHistoryId: selectedPenaltyHistory ? selectedPenaltyHistory.id : undefined,
            amount: amountForPenaltyPayments,
            note,
            bankTransactionId: props.bankTransaction?.id
        };
        invoiceService.payInvoicePenalty(props.selectedInvoice?.id, data)
            .then(closeInvoicePenaltiesDialog)
    }

    const handlePenaltyPaymentDateSelection = (e: FormEvent<Date>) => {
        setSelectedDateFormPenaltyPayments(e.value!);
        const selectedDate: Date = e.value as Date;
        //Check if the selected date is today
        const today = new Date();
        if (
            today.getFullYear() === selectedDate.getFullYear() &&
            today.getMonth() === selectedDate.getMonth() &&
            today.getDate() === selectedDate.getDate()
        ) {
            setSelectedPenaltyHistory(undefined);
            setAmountForPenaltyPayments(props.selectedInvoice.penalty);
        } else {
            invoiceService.invoicePenaltyHistoryByDate(selectedDate, props.selectedInvoice?.id!)
                .then(resp => {
                    if (resp) {
                        setSelectedPenaltyHistory(resp);
                        setAmountForPenaltyPayments(resp.penaltyBeforeAccumulation);
                    } else setSelectedPenaltyHistory(undefined);
                })
        }
    }

    const getColumnTemplate = () => {
        return {
            dateCreated: (rowData: InvoicePenaltyHistoryModel) => moment(rowData.dateCreated).format("DD-MM-YYYY HH:mm:ss"),
            penaltyBeforeAccumulation: (rowData: InvoicePenaltyHistoryModel) => rowData.penaltyBeforeAccumulation + " €",
            penaltyAfterAccumulation: (rowData: InvoicePenaltyHistoryModel) => rowData.penaltyAfterAccumulation + " €",
            penaltyAccumulation: (rowData: InvoicePenaltyHistoryModel) => rowData.penaltyAccumulation + " €"
        }
    }

    const handleExternalFilter = () => {
        return {
            dateCreated: (rowData: InvoicePenaltyHistoryModel, filterValue: string) => {
                const formattedDate = moment(rowData.dateCreated).format("DD-MM-YYYY HH:mm:ss");
                return formattedDate.includes(filterValue);
            }
        }
    }

    return <>
        <Dialog header={f({id: 'penaltiesForInvoice'}, {invoiceNumber: props.selectedInvoice?.invoiceNumber})}
                onHide={() => props.closeDialog()}
                breakpoints={{'960px': '75vw', '640px': '100vw'}}
                visible={props.selectedInvoice !== undefined}>

            {!props.previewOnly &&
                <>
                    <h4>{f({id: "penaltyRepayment"})}</h4>


                    <div className={"grid p-fluid justify-content-center mt-4"}>
                        <div className={"col-12 md:col-4"}>
                            <span className="p-float-label">
                                <Calendar value={selectedDateFormPenaltyPayments} maxDate={new Date()} minDate={minDate}
                                          dateFormat={'dd-mm-yy'}
                                          onChange={(e) => handlePenaltyPaymentDateSelection(e)}/>
                                <label>{f({id: "toDate"})}</label>
                            </span>
                        </div>

                        <div className={"col-12 md:col-4"}>
                            <span className="p-float-label">
                                <InputNumber value={amountForPenaltyPayments}
                                             max={selectedPenaltyHistory ? selectedPenaltyHistory.penaltyBeforeAccumulation : props.selectedInvoice.penalty}
                                             maxFractionDigits={2}
                                             mode={"decimal"}
                                             useGrouping={false}
                                             onChange={(e) => setAmountForPenaltyPayments(e.value!)}/>
                                <label>{f({id: "amount"})}</label>
                            </span>
                        </div>

                        <div className={"col-12 md:col-4"}>
                            <span className="p-float-label">
                                <InputText value={note} onChange={(e) => setNote(e.target.value)}/>
                                <label>{f({id: "note"})}</label>
                            </span>
                        </div>

                        <div className={"col-12 md:col-6"}>
                            <Button label={f({id: "repayment"})}
                                    disabled={amountForPenaltyPayments === undefined || !selectedDateFormPenaltyPayments}
                                    onClick={payPenaltiesForInvoiceForDate}/>
                        </div>

                        <div className={"col-12 md:col-6"}>
                            <Button label={f({id: "cancel"})} onClick={closeInvoicePenaltiesDialog}/>
                        </div>
                    </div>
                </>

            }

            <ReactiveTable
                columnOrder={["dateCreated", "penaltyBeforeAccumulation", "penaltyAccumulation", "penaltyAfterAccumulation"]}
                columnTemplate={getColumnTemplate()}
                specialLabels={{dateCreated: f({id: "date"})}}
                externalFilters={handleExternalFilter()}
                data={invoicePenaltyHistory}/>

        </Dialog>
    </>
};

export default PenaltyPaymentForInvoice
